import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  User, Mail, Phone, Calendar, FileText, Award, FileCheck,
  Download, ChevronLeft, CheckCircle, Clock, AlertCircle
} from 'lucide-react';
import Badge from '@/components/Badge';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MyRecordsPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [records, setRecords] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    loadRecords();
  }, [isAuthenticated]);

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const loadRecords = async () => {
    try {
      const response = await axios.get(`${API}/progress/my-records`, { headers: getAuthHeaders() });
      setRecords(response.data);
    } catch (error) {
      console.error('Failed to load records:', error);
      toast.error('Failed to load your records');
    } finally {
      setLoading(false);
    }
  };

  const downloadDocument = async (docType) => {
    try {
      const response = await axios.get(`${API}/progress/my-records/document/${docType}`, { headers: getAuthHeaders() });
      
      if (response.data && response.data.data) {
        // Create blob from base64 data
        const byteCharacters = atob(response.data.data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: response.data.content_type });
        
        // Download the file
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = response.data.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary">Loading your records...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              <ChevronLeft className="w-5 h-5 mr-2" />
              Back to Dashboard
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="w-6 h-6" />
            <span className="font-bold" style={{ fontFamily: 'Barlow Condensed' }}>MY RECORDS</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Compliance Notice */}
        <div className="bg-primary/5 border border-primary/20 rounded-sm p-4 mb-8">
          <h2 className="font-bold text-primary mb-2 flex items-center gap-2">
            <FileCheck className="w-5 h-5" />
            Colorado Compliance Records
          </h2>
          <p className="text-sm text-muted-foreground">
            Per Colorado DOI requirements, these records are retained for 5 years. You can download copies of your 
            documents for your own records or to provide to employers.
          </p>
        </div>

        {/* Personal Information */}
        <div className="bg-card border border-border rounded-sm p-6 mb-6">
          <h3 className="font-bold text-lg mb-4 flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
            <User className="w-5 h-5 text-primary" />
            PERSONAL INFORMATION
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Full Name</p>
                <p className="font-medium">{records?.personal_info?.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Email</p>
                <p className="font-medium">{records?.personal_info?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Phone</p>
                <p className="font-medium">{records?.personal_info?.phone || 'Not provided'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Registered</p>
                <p className="font-medium">{formatDate(records?.personal_info?.registered_at)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Documents Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Certificate */}
          <div className="bg-card border border-border rounded-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold flex items-center gap-2">
                <Award className="w-5 h-5 text-primary" />
                Certificate of Completion
              </h3>
              {records?.certificate ? (
                <span className="text-xs bg-green-500/10 text-green-500 px-2 py-1 rounded-sm">ISSUED</span>
              ) : (
                <span className="text-xs bg-yellow-500/10 text-yellow-500 px-2 py-1 rounded-sm">PENDING</span>
              )}
            </div>
            
            {records?.certificate ? (
              <div className="space-y-3">
                <div className="p-3 bg-green-500/5 border border-green-500/20 rounded-sm">
                  <p className="text-sm font-mono font-bold text-green-600">
                    #{records.certificate.certificate_number}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Issued: {formatDate(records.certificate.issued_at)}
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => navigate('/certificate-preview')}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  View Certificate
                </Button>
              </div>
            ) : (
              <div className="text-center py-4">
                <Clock className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">
                  Complete all course requirements to earn your certificate
                </p>
              </div>
            )}
          </div>

          {/* Background Check */}
          <div className="bg-card border border-border rounded-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold flex items-center gap-2">
                <FileCheck className="w-5 h-5 text-primary" />
                Background Check
              </h3>
              {records?.background_check?.status === 'completed' ? (
                <span className="text-xs bg-green-500/10 text-green-500 px-2 py-1 rounded-sm">COMPLETED</span>
              ) : (
                <span className="text-xs bg-yellow-500/10 text-yellow-500 px-2 py-1 rounded-sm">PENDING</span>
              )}
            </div>
            
            {records?.background_check ? (
              <div className="space-y-3">
                <div className="p-3 bg-secondary rounded-sm">
                  <p className="text-sm">{records.background_check.filename}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Uploaded: {formatDate(records.background_check.uploaded_at)}
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => downloadDocument('background_check')}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Copy
                </Button>
              </div>
            ) : (
              <div className="text-center py-4">
                <AlertCircle className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">
                  Background check will be uploaded by administrator after processing
                </p>
              </div>
            )}
          </div>

          {/* Final Exam Results */}
          <div className="bg-card border border-border rounded-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Final Exam
              </h3>
              {records?.final_exam?.passed ? (
                <span className="text-xs bg-green-500/10 text-green-500 px-2 py-1 rounded-sm">PASSED</span>
              ) : records?.final_exam ? (
                <span className="text-xs bg-red-500/10 text-red-500 px-2 py-1 rounded-sm">NOT PASSED</span>
              ) : (
                <span className="text-xs bg-secondary text-muted-foreground px-2 py-1 rounded-sm">NOT TAKEN</span>
              )}
            </div>
            
            {records?.final_exam ? (
              <div className="space-y-3">
                <div className={`p-3 rounded-sm ${records.final_exam.passed ? 'bg-green-500/5 border border-green-500/20' : 'bg-red-500/5 border border-red-500/20'}`}>
                  <p className="text-2xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
                    {records.final_exam.score}%
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {records.final_exam.passed ? 'Passing score achieved (75% required)' : 'Did not meet 75% passing score'}
                  </p>
                </div>
                <p className="text-xs text-muted-foreground">
                  Completed: {formatDate(records.final_exam.submitted_at)}
                </p>
              </div>
            ) : (
              <div className="text-center py-4">
                <Clock className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">
                  Complete all chapters to unlock the final exam
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-8 text-center text-sm text-muted-foreground">
          <p>
            Need assistance with your records? Contact <a href="mailto:support@coloradobailenforcement.com" className="text-primary hover:underline">support@coloradobailenforcement.com</a>
          </p>
        </div>
      </main>
    </div>
  );
};

export default MyRecordsPage;
